import React, { useState, useEffect } from 'react'
import { X, Check, Loader, AlertCircle, Users, Plus, Trash2 } from 'lucide-react'
import './CareTeamEditModal.css'

/**
 * CareTeamEditModal - Modal for editing care team information
 * 
 * Props:
 * - isOpen: Boolean to control modal visibility
 * - onClose: Function to close the modal
 * - careTeam: Current care team data object
 * - onSave: Async function(careTeamUpdates) => Promise - called to save changes
 * - patientName: Patient name for display
 */
function CareTeamEditModal({ isOpen, onClose, careTeam = {}, onSave, patientName }) {
  const [formData, setFormData] = useState({
    primary_physician: '',
    specialists: [],
    care_coordinator: '',
    preferred_contact_method: 'phone'
  })
  const [newSpecialist, setNewSpecialist] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)

  // Contact method options
  const contactMethods = [
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'portal', label: 'Patient Portal' },
    { value: 'text', label: 'Text Message' }
  ]

  // Initialize form with current care team data
  useEffect(() => {
    if (isOpen && careTeam) {
      setFormData({
        primary_physician: careTeam.primary_physician || '',
        specialists: careTeam.specialists ? [...careTeam.specialists] : [],
        care_coordinator: careTeam.care_coordinator || '',
        preferred_contact_method: careTeam.preferred_contact_method || 'phone'
      })
      setNewSpecialist('')
      setError(null)
    }
  }, [isOpen, careTeam])

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, isSaving, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddSpecialist = () => {
    if (newSpecialist.trim()) {
      setFormData(prev => ({
        ...prev,
        specialists: [...prev.specialists, newSpecialist.trim()]
      }))
      setNewSpecialist('')
    }
  }

  const handleRemoveSpecialist = (index) => {
    setFormData(prev => ({
      ...prev,
      specialists: prev.specialists.filter((_, i) => i !== index)
    }))
  }

  const handleSpecialistKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddSpecialist()
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)

    try {
      // Build update object, only including non-empty values
      const updates = {}
      
      if (formData.primary_physician.trim()) {
        updates.primary_physician = formData.primary_physician.trim()
      }
      
      if (formData.specialists.length > 0) {
        updates.specialists = formData.specialists
      }
      
      if (formData.care_coordinator.trim()) {
        updates.care_coordinator = formData.care_coordinator.trim()
      }
      
      if (formData.preferred_contact_method) {
        updates.preferred_contact_method = formData.preferred_contact_method
      }

      await onSave({ care_team: updates })
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to save care team changes')
    } finally {
      setIsSaving(false)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="care-team-modal-overlay" onClick={handleBackdropClick}>
      <div className="care-team-modal">
        <div className="care-team-modal-header">
          <div className="care-team-modal-title">
            <Users size={20} />
            <h2>Edit Care Team</h2>
          </div>
          <button
            className="care-team-modal-close"
            onClick={onClose}
            disabled={isSaving}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {patientName && (
          <div className="care-team-modal-patient">
            Patient: <strong>{patientName}</strong>
          </div>
        )}

        <div className="care-team-modal-body">
          {/* Primary Physician */}
          <div className="care-team-form-group">
            <label htmlFor="primary_physician">Primary Physician</label>
            <input
              id="primary_physician"
              type="text"
              value={formData.primary_physician}
              onChange={(e) => handleInputChange('primary_physician', e.target.value)}
              placeholder="e.g., Dr. Sarah Johnson"
              disabled={isSaving}
            />
          </div>

          {/* Care Coordinator */}
          <div className="care-team-form-group">
            <label htmlFor="care_coordinator">Care Coordinator</label>
            <input
              id="care_coordinator"
              type="text"
              value={formData.care_coordinator}
              onChange={(e) => handleInputChange('care_coordinator', e.target.value)}
              placeholder="e.g., Mary Smith, RN"
              disabled={isSaving}
            />
          </div>

          {/* Preferred Contact Method */}
          <div className="care-team-form-group">
            <label htmlFor="preferred_contact_method">Preferred Contact Method</label>
            <select
              id="preferred_contact_method"
              value={formData.preferred_contact_method}
              onChange={(e) => handleInputChange('preferred_contact_method', e.target.value)}
              disabled={isSaving}
            >
              {contactMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          {/* Specialists */}
          <div className="care-team-form-group">
            <label>Specialists</label>
            <div className="specialists-list">
              {formData.specialists.map((specialist, index) => (
                <div key={index} className="specialist-item">
                  <span>{specialist}</span>
                  <button
                    type="button"
                    className="specialist-remove"
                    onClick={() => handleRemoveSpecialist(index)}
                    disabled={isSaving}
                    title="Remove specialist"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {formData.specialists.length === 0 && (
                <p className="no-specialists">No specialists assigned</p>
              )}
            </div>
            <div className="add-specialist-row">
              <input
                type="text"
                value={newSpecialist}
                onChange={(e) => setNewSpecialist(e.target.value)}
                onKeyDown={handleSpecialistKeyDown}
                placeholder="Add specialist (e.g., Dr. Chen - Cardiology)"
                disabled={isSaving}
              />
              <button
                type="button"
                className="btn-add-specialist"
                onClick={handleAddSpecialist}
                disabled={isSaving || !newSpecialist.trim()}
                title="Add specialist"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {error && (
            <div className="care-team-error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        <div className="care-team-modal-footer">
          <button
            className="btn-cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader size={16} className="spinner" />
                Saving...
              </>
            ) : (
              <>
                <Check size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CareTeamEditModal